import { Button, Center, HStack, VStack } from '@chakra-ui/react';
import { DraggablePanel } from '@components/ui/draggable';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { selectIsNeutralSensorJamming } from '@redux/situation/monitoring.selectors';
import { selectNeutralInfo } from '@redux/situation/situation.selectors';
import { usePrepareEngagedPositionMutation, usePrepareEngagedTargetMutation } from '@services/c2/engagement.api';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { PopupPositionData } from '@/types/popup.types';

import EngagementPositionHeader from './EngagementPositionHeader';
import EngagementReports from './EngagementReports';
import EngagementSelector from './EngagementSelector';
import EngagementTargetHeader from './EngagementTargetHeader';

export default function EngagementPopup({ mapConstraintsRef, top, left, onClose }: Readonly<PopupPositionData>) {
  const neutralInfo = useSelectorWithReplayMode(selectNeutralInfo);
  const [prepareEngagedPosition] = usePrepareEngagedPositionMutation();
  const [prepareEngagedTarget] = usePrepareEngagedTargetMutation();
  const [openReport, setOpenReport] = useState(false);
  const isSensorJamming = useSelectorWithReplayMode(selectIsNeutralSensorJamming);

  const handleClose = () => {
    if (openReport && neutralInfo !== null) {
      if (neutralInfo.targetId !== null) {
        prepareEngagedTarget(null).unwrap().then(onClose);
      } else {
        prepareEngagedPosition(null).unwrap().then(onClose);
      }
    } else {
      onClose();
    }
  };

  if (neutralInfo === null || (neutralInfo.targetId === null && neutralInfo.coordinates === null)) {
    onClose();
    return;
  }

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left}>
      <DraggablePanel.Header onClose={handleClose}>
        <FormattedMessage id="components.popups.engagement.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={530} minHeight={462} maxHeight={706}>
        <VStack backgroundColor="neutral.900" gap={0.25}>
          <HStack width="100%" height="120px" backgroundColor="neutral.700" paddingX={4} gap={2}>
            {neutralInfo.targetId !== null ? (
              <EngagementTargetHeader targetId={neutralInfo.targetId} />
            ) : (
              <EngagementPositionHeader coordinates={neutralInfo.coordinates} />
            )}
          </HStack>
          {openReport ? (
            <EngagementReports id={neutralInfo.engagementSequenceDto?.id ?? null} />
          ) : (
            <>
              <EngagementSelector neutralInfo={neutralInfo} />
              <Center padding={4} backgroundColor="neutral.700" width="100%">
                <Button variant="primary" disabled={isSensorJamming} onClick={() => setOpenReport(true)}>
                  <FormattedMessage id="components.popups.engagement.finish" />
                </Button>
              </Center>
            </>
          )}
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
